<template>
  <div class="col-12">
    <div class="card mb-5 " style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <div class="d-flex justify-content-between align-items-center">
            <h6 class="text-white text-capitalize ps-3 pe-3 mb-0">
              {{ t('employees.edit_employee_title') }}
            </h6>
          </div>
        </div>
      </div>

      <LoadingSpinner v-if="isLoading" :message="t('employees.loading_employee')" />
      <div v-else-if="employee" class="card-body px-0 pb-2 pe-3">
        <div class="row justify-content-center">
          <div class="col-12">
            <EmployeeForm
              ref="employeeForm"
              :initial-data="employee"
              :is-loading="isSubmitting"
              :is-edit-mode="true"
              @submit="updateEmployee"
              @cancel="navigateBack"
              @validation-error="handleValidationError"
              @update:form-data="handleFormUpdate"
            />

            <div class="d-flex gap-3 mt-3 ms-3">
              <button type="button" class="btn btn-primary btn-sm flex-grow-1" :disabled="isSubmitting || !hasChanges" @click.prevent="submitForm">
                <span v-if="isSubmitting"><i class="fas fa-spinner fa-spin ms-2"></i>{{ t('employees.saving') }}</span>
                <span v-else><i class="fas fa-edit ms-2"></i>{{ t('employees.save_changes') }}</span>
              </button>
              <button type="button" class="btn btn-outline-secondary btn-sm flex-grow-1" @click="navigateBack" :disabled="isSubmitting">
                <i class="fas fa-times ms-2"></i>{{ t('employees.cancel') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import EmployeeForm from "@/components/features/employees/EmployeeForm.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
const toast = useToast();

const isLoading = ref(false);
const isSubmitting = ref(false);
const employeeForm = ref(null);
const originalEmployeeData = ref(null);
const currentFormData = ref(null);

const hasChanges = computed(() => {
  if (!originalEmployeeData.value || !currentFormData.value) return false;
  return Object.keys(currentFormData.value).some(key => {
    const currentValue = currentFormData.value[key];
    const originalValue = originalEmployeeData.value[key];
    if (key === 'salary') {
      return Number(currentValue || 0) !== Number(originalValue || 0);
    }
    return JSON.stringify(currentValue) !== JSON.stringify(originalValue);
  });
});

const handleFormUpdate = (newData) => { currentFormData.value = { ...newData }; };

const employee = computed(() => {
  const data = store.state.employee.currentEmployee;
  if (data) {
    const rawActive = data.is_active;
    const isActive = rawActive == null ? true : (rawActive !== false && rawActive !== 0 && rawActive !== "0");
    return {
      ...data,
      password: "",
      password_confirmation: "",
      salary: data.salary != null ? Number(data.salary) : "",
      nationality_id: data.nationality?.id || "",
      job_title_id: data.job_title_id || data.job_title?.id || "",
      hire_date: data.hire_date || "",
      image_url: data.image_url,
      is_active: isActive,
    };
  }
  return null;
});

watch(employee, (newData) => {
  if (newData && !originalEmployeeData.value) {
    originalEmployeeData.value = { ...newData };
    currentFormData.value = { ...newData };
  }
}, { immediate: true });

const submitForm = async () => {
  if (!employeeForm.value) return;
  isSubmitting.value = true;
  try {
    await employeeForm.value.submitForm();
  } finally {
    isSubmitting.value = false;
  }
};

const fetchEmployee = async () => {
  isLoading.value = true;
  try {
    await store.dispatch("employee/fetchEmployee", route.params.id);
  } catch (error) {
    toast.error(t("employees.loading_failed"));
    router.push({ name: "employees.index" });
  } finally {
    isLoading.value = false;
  }
};

const updateEmployee = async (formData) => {
  if (!hasChanges.value) {
    toast.info(t("employees.no_changes"));
    return;
  }

  isSubmitting.value = true;
  try {
    await store.dispatch("employee/updateEmployee", { id: route.params.id, employeeData: formData });
    toast.success(t("employees.updated_successfully"));
    router.push({ name: "employees.index" });
  } catch (error) {
    if (error.response?.status === 422 && error.response?.data?.errors) {
      employeeForm.value?.setBackendErrors(error.response.data.errors);
      const firstError = Object.values(error.response.data.errors)[0]?.[0];
      if (firstError) toast.error(firstError);
    } else {
      toast.error(error.response?.data?.message || t("employees.update_failed"));
    }
  } finally {
    isSubmitting.value = false;
  }
};

const handleValidationError = () => toast.warning(t("employees.fill_required_fields"));
const navigateBack = () => router.push({ name: "employees.index" });

onMounted(() => fetchEmployee());
</script>
